// Types for the editor-entered STV results state (Stage 3C).
//
// Persisted in Postgres (table `electionday_state`, row kind='results',
// payload jsonb). One JSONB document holds all 13 districts; deliberately
// isolated from editorial / sampling / turnout so concurrent saves can't
// clobber each other.
//
// ── DESIGN: auto-derive election status, operator overrides win ──
//
// The deriver runs the textbook STV status algorithm against the
// operator-typed running totals + non-transferable:
//
//   1. QUOTA ELECTION — any continuing candidate whose runningTotal
//      ≥ quota at the end of a round flips to elected_by_quota and
//      stays elected.
//   2. EXCLUSION — a continuing candidate whose entry disappears from
//      the round's totals (or drops to 0) from a positive prior is
//      flipped to excluded. Matches the board: the commission picks
//      the lowest continuing and that candidate's column goes blank.
//   3. WITHOUT-QUOTA ELECTION (the Maltese commission's stopping
//      convention) — when seats still remain and the LOCK CONDITION
//      holds — i.e. the leader's lead among continuing candidates
//      exceeds the sum of every still-transferable vote that could
//      flow to the highest non-elected candidate (other continuing
//      candidates' totals + any pending surplus from elected_by_quota
//      candidates over quota) — the top N continuing become
//      elected_without_quota and the remaining (K-N) become
//      not_elected. Closes 12/13 of the 2022 districts.
//
// All three rules fire automatically as the operator enters totals.
// The operator can OVERRIDE any auto-derived status by entering an
// explicit value in `statusByCandidate` for that round — operator
// entry always wins. Use it when the board records something the
// math doesn't predict, or when a typo's been fixed and the previous
// override should be removed.
//
// DERIVED:
//   · quota (a pure validVotes calculation)
//   · per-candidate status (auto + operator override on top)
//   · per-candidate delta (running totals diff)
//   · totals check (Σ running totals + non-transferable vs validVotes)
//   · electedSeats (ordered by election round; ties by R1 running total)
//
// ── COUNT RULES (background) ──────────────────────────────────────
//
// 1. Quota = ⌊validVotes ÷ (seats + 1)⌋ + 1. Seats = 5.
// 2. The commission elects a candidate "by quota" the moment running
//    total ≥ quota, and "without quota" as last-standing when no
//    transferable votes remain that could change the order.
// 3. Non-transferable votes are their own pile, NEVER a candidate row.
// 4. Casual elections are out of scope.

export const SEATS_PER_DISTRICT = 5;
export const DISTRICT_NUMBERS: readonly number[] = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13,
];

// ── STORED shapes ───────────────────────────────────────────────

/** Status of a single candidate within a district. */
export type CandidateStatus =
  | 'continuing'
  | 'elected_by_quota'
  | 'elected_without_quota'
  /** Continuing through the close of count but not elected. Distinct
   *  from `excluded` — the commission did not exclude them mid-count;
   *  they simply weren't elected. Their final votes typically rest
   *  with the non-transferable pile after the count stops. */
  | 'not_elected'
  /** Formally excluded mid-count by the commission (lowest-ranked,
   *  votes redistributed). */
  | 'excluded';

export const CANDIDATE_STATUSES: readonly CandidateStatus[] = [
  'continuing',
  'elected_by_quota',
  'elected_without_quota',
  'not_elected',
  'excluded',
];

/** What the editor types for a single round. */
export interface StoredRound {
  roundNumber: number;          // 1..N
  /** Candidate whose surplus distributed / who was excluded this
   *  round. Optional caption hint; the editor can leave blank. */
  actorId: string | null;
  /** Keyed by candidateId (DB row id, as string). Only candidates
   *  whose total moved in this round need an entry — earlier rounds'
   *  totals carry forward for status purposes. */
  runningTotalByCandidate: Record<string, number>;
  /** Per-candidate OVERRIDE of the auto-derived status. Optional.
   *  Operator entry wins over the math whenever present at this
   *  round. To revert a prior override back to "let the math
   *  decide", remove the entry (or set it to the value the math
   *  would produce). */
  statusByCandidate?: Record<string, CandidateStatus>;
  /** Running total of exhausted ballots at the end of this round. */
  nonTransferable: number;
}

/** Per-district stored state.
 *
 *  Three editor-supplied counting inputs drive the auto-derivation of
 *  the valid-votes total (and therefore the Droop quota) — only if
 *  `validVotes` itself isn't set as a manual override:
 *
 *    registeredVoters  → prefilled from src/data/malta_districts_2022.geojson
 *                        (results_2022.registered) the first time the
 *                        editor lands on the district. Stays null in
 *                        the stored doc until they explicitly confirm,
 *                        so the admin UI can flag the prefill as
 *                        "from 2022 — confirm".
 *    turnoutPct        → prefilled from useElectionDayTurnout()'s
 *                        pct_10pm for that district. Stays null in
 *                        the stored doc until confirmed; UI surfaces
 *                        the turnout-state value loudly until then.
 *    invalidVotes      → editor-entered (no prefill — invalid ballots
 *                        are an election-day artefact, no historical
 *                        baseline that's meaningful here).
 *
 *  effectiveValidVotes() merges the three into a computed valid-votes
 *  figure, deferring to `validVotes` if the editor has set it
 *  explicitly. deriveDistrictCount() uses effectiveValidVotes for the
 *  quota calculation. */
export interface ResultsDistrict {
  /** Registered voters in the district. Editor-confirmed value.
   *  Null = stored doc hasn't captured a confirmed figure yet; the
   *  admin UI surfaces the 2022 geojson value as a "needs review"
   *  prefill. */
  registeredVoters?: number | null;
  /** Closing turnout % for the district. Editor-confirmed value.
   *  Null = stored doc hasn't captured a confirmed figure yet; the
   *  admin UI prefills from the live turnout state (pct_10pm). */
  turnoutPct?: number | null;
  /** Invalid ballots counted in the district. Editor-entered. */
  invalidVotes?: number | null;
  /** Manual override for the valid-votes figure. When set, takes
   *  precedence over the auto-computed (registered × turnout%
   *  − invalid). When null, the auto-compute is used (or null if
   *  any of the three inputs above are still missing). */
  validVotes: number | null;
  rounds: StoredRound[];
}

/** Top-level payload — keyed by district number ("1".."13"). */
export interface ResultsState {
  districts: Partial<Record<string, ResultsDistrict>>;
  updatedAt: string;
}

export function emptyResultsState(): ResultsState {
  return {
    districts: {},
    updatedAt: new Date(0).toISOString(),
  };
}

export function emptyDistrict(): ResultsDistrict {
  return {
    registeredVoters: null,
    turnoutPct: null,
    invalidVotes: null,
    validVotes: null,
    rounds: [],
  };
}

/** Compute the effective valid-votes figure for a district:
 *    1. If `validVotes` is set, use it (editor override).
 *    2. Else if all three inputs are present, compute
 *       `floor(registeredVoters × turnoutPct ÷ 100) − invalidVotes`.
 *    3. Else null.
 *
 *  This is what `deriveDistrictCount()` reads — keeping the deriver
 *  unchanged in shape while letting the new admin UI flow editor
 *  inputs through to the quota calculation. */
export function effectiveValidVotes(d: ResultsDistrict | undefined): number | null {
  if (!d) return null;
  if (
    d.validVotes !== null &&
    d.validVotes !== undefined &&
    Number.isFinite(d.validVotes)
  ) {
    return d.validVotes;
  }
  const r = d.registeredVoters;
  const t = d.turnoutPct;
  const i = d.invalidVotes;
  if (
    typeof r === 'number' && Number.isFinite(r) && r > 0 &&
    typeof t === 'number' && Number.isFinite(t) && t > 0 &&
    typeof i === 'number' && Number.isFinite(i) && i >= 0
  ) {
    const cast = Math.floor((r * t) / 100);
    const valid = cast - i;
    return valid > 0 ? valid : null;
  }
  return null;
}

// ── DERIVED shapes (what render code reads) ─────────────────────

export type RoundType =
  | 'first_count'
  | 'surplus_distribution'
  | 'exclusion'
  | 'fill';

export interface DerivedRound {
  roundNumber: number;
  type: RoundType;
  actorId: string | null;
  perCandidate: Array<{
    candidateId: string;
    runningTotal: number;
    /** delta vs previous round; null at round 1 */
    delta: number | null;
    status: CandidateStatus;
  }>;
  nonTransferable: number;
  /** Σ(candidate runningTotals, excluding excluded candidates) +
   *  nonTransferable. Should equal validVotes — mismatch is the
   *  "totals check" warning trigger. */
  totalsCheck: number;
}

export type DistrictStatus = 'pending' | 'in_progress' | 'complete';

export interface DerivedDistrict {
  districtId: number;
  validVotes: number | null;
  /** Droop: ⌊validVotes ÷ 6⌋ + 1. Null if validVotes not yet entered. */
  quota: number | null;
  rounds: DerivedRound[];
  /** candidateIds elected, in the order their status flipped. Length
   *  = seats once the district is complete. */
  electedSeats: string[];
  status: DistrictStatus;
}

// ── DERIVER ──────────────────────────────────────────────────────

/** Compute the Droop quota. Returns null if validVotes is null. */
export function deriveQuota(validVotes: number | null): number | null {
  if (validVotes === null || !Number.isFinite(validVotes) || validVotes <= 0) {
    return null;
  }
  return Math.floor(validVotes / (SEATS_PER_DISTRICT + 1)) + 1;
}

/** Walk the stored district round-by-round, deriving each candidate's
 *  status from the math (quota election, exclusion, without-quota
 *  lock) and applying operator overrides on top. */
export function deriveDistrictCount(
  districtId: number,
  stored: ResultsDistrict | undefined,
  candidateIds: readonly string[],
): DerivedDistrict {
  // Use the effective valid-votes figure (manual override OR auto-
  // computed from registered × turnout − invalid) so the quota
  // picks up the new admin-header prefills without the deriver
  // needing to know about them.
  const validVotes = effectiveValidVotes(stored);
  const quota = deriveQuota(validVotes);

  const sourceRounds = stored?.rounds ?? [];
  const sortedRounds = [...sourceRounds].sort(
    (a, b) => a.roundNumber - b.roundNumber,
  );

  // Carried per-candidate state across rounds.
  const status: Record<string, CandidateStatus> = {};
  const electedAt: Record<string, number> = {};
  for (const cid of candidateIds) status[cid] = 'continuing';

  const derivedRounds: DerivedRound[] = [];
  let prevByCand: Record<string, number> = {};
  // A candidate the operator has never entered a total for is omitted
  // from the per-round rows so the table doesn't fill with phantom
  // zeros for two-district candidates who didn't actually contest here.
  const hasAppeared = new Set<string>();

  for (let roundIdx = 0; roundIdx < sortedRounds.length; roundIdx++) {
    const round = sortedRounds[roundIdx];
    const isLastRound = roundIdx === sortedRounds.length - 1;
    const totals = round.runningTotalByCandidate || {};
    const overrides = round.statusByCandidate || {};
    for (const cid of Object.keys(totals)) hasAppeared.add(cid);

    // ── PASS 1: per-candidate auto-derivation (quota, exclusion) ──
    // For every candidate still continuing at the start of this round,
    // look at their totals and the math decides. Already-terminal
    // statuses (elected_by_quota, elected_without_quota, not_elected,
    // excluded) carry forward unchanged.
    for (const cid of candidateIds) {
      if (status[cid] !== 'continuing') continue;
      const incoming = totals[cid];
      const prev = prevByCand[cid];

      // Exclusion: candidate's column went blank (no incoming) or
      // dropped to 0 with a positive prior. The commission excluded
      // them this round; their votes are now being redistributed.
      if (
        (incoming === undefined && prev !== undefined && prev > 0) ||
        (incoming === 0 && prev !== undefined && prev > 0)
      ) {
        setStatus(status, electedAt, cid, 'excluded', round.roundNumber);
        continue;
      }

      // Quota election: any positive total ≥ quota flips them.
      if (quota !== null && incoming !== undefined && incoming >= quota) {
        setStatus(status, electedAt, cid, 'elected_by_quota', round.roundNumber);
      }
    }

    // ── PASS 2: without-quota lock condition ──
    // Mirrors the Maltese commission's stopping convention: after the
    // count finishes its scheduled surplus distributions, if no further
    // transfers could change the order of continuing candidates, the
    // top N elect without quota and the rest are not elected.
    //
    // Fires ONLY at the LAST entered round — matches the commission's
    // behaviour (they always run pending surpluses first). Quota
    // elections and exclusions in PASS 1 fire on any round; only this
    // closing call is gated on "no more data coming".
    const electedCount = Object.values(status).filter(
      (s) => s === 'elected_by_quota' || s === 'elected_without_quota',
    ).length;
    const seatsRemaining = SEATS_PER_DISTRICT - electedCount;
    if (isLastRound && seatsRemaining > 0) {
      // Continuing pool, sorted by current rt desc.
      const continuingPool = candidateIds
        .filter((cid) => status[cid] === 'continuing')
        .map((cid) => {
          const incoming = totals[cid];
          const prev = prevByCand[cid];
          const rt = incoming !== undefined ? incoming : (prev ?? 0);
          return { cid, rt };
        })
        .filter((c) => hasAppeared.has(c.cid))
        .sort((a, b) => b.rt - a.rt);

      const K = continuingPool.length;
      const N = seatsRemaining;
      if (K > 0 && K >= N) {
        // Pending surplus from elected_by_quota candidates over quota
        // — worst-case extra transferable supply for any threat.
        let pendingSurplus = 0;
        if (quota !== null) {
          for (const cid of candidateIds) {
            if (status[cid] !== 'elected_by_quota') continue;
            const rt = totals[cid] !== undefined ? totals[cid] : (prevByCand[cid] ?? 0);
            if (rt > quota) pendingSurplus += rt - quota;
          }
        }

        let lockHolds = false;
        if (K === N) {
          // Strict equality: by Schedule 13, elect all remaining.
          lockHolds = true;
        } else {
          // K > N: leader = continuingPool[N-1], threat = [N], others = [N+1..K-1]
          const leader = continuingPool[N - 1];
          const threat = continuingPool[N];
          const othersSum = continuingPool
            .slice(N + 1)
            .reduce((s, c) => s + c.rt, 0);
          const threatMax = threat.rt + othersSum + pendingSurplus;
          lockHolds = leader.rt > threatMax;
        }

        if (lockHolds) {
          for (let i = 0; i < N && i < K; i++) {
            setStatus(
              status,
              electedAt,
              continuingPool[i].cid,
              'elected_without_quota',
              round.roundNumber,
            );
          }
          for (let i = N; i < K; i++) {
            setStatus(
              status,
              electedAt,
              continuingPool[i].cid,
              'not_elected',
              round.roundNumber,
            );
          }
        }
      }
    }

    // ── PASS 3: operator overrides win ──
    // Apply any explicit operator entry on top of auto-derived
    // statuses. Empty/missing overrides leave the math in place.
    for (const [cid, override] of Object.entries(overrides)) {
      if (!isCandidateStatus(override)) continue;
      setStatus(status, electedAt, cid, override, round.roundNumber);
    }

    // ── Build the per-candidate row for this round ──
    const perCandidate: DerivedRound['perCandidate'] = [];
    let sumRunningTotals = 0;
    for (const cid of candidateIds) {
      const incoming = totals[cid];
      const prev = prevByCand[cid];
      const rt = incoming !== undefined ? incoming : prev;
      if (rt === undefined && !hasAppeared.has(cid)) continue;

      const effectiveRt = rt ?? 0;
      const delta =
        incoming === undefined || prev === undefined
          ? null
          : incoming - prev;
      const curStatus = status[cid] ?? 'continuing';

      // Excluded candidates' votes have been redistributed already —
      // don't sum their carry-forward into totalsCheck.
      if (curStatus !== 'excluded') sumRunningTotals += effectiveRt;

      perCandidate.push({
        candidateId: cid,
        runningTotal: effectiveRt,
        delta,
        status: curStatus,
      });
    }

    // ── Round type ──
    // Look at the status flips that landed at THIS round.
    let type: RoundType;
    if (round.roundNumber === 1) type = 'first_count';
    else {
      const flippedThisRound = Object.entries(electedAt)
        .filter(([, r]) => r === round.roundNumber)
        .map(([cid]) => status[cid]);
      const excludedThisRound = candidateIds.some(
        (cid) =>
          status[cid] === 'excluded' &&
          // crude detection: candidate showed up in the row table with
          // excluded status this round and wasn't excluded before.
          perCandidate.some((p) => p.candidateId === cid && p.status === 'excluded'),
      );
      if (
        flippedThisRound.some(
          (s) => s === 'elected_without_quota' || s === undefined,
        ) ||
        Object.values(overrides).some(
          (s) => s === 'elected_without_quota' || s === 'not_elected',
        )
      ) {
        type = 'fill';
      } else if (excludedThisRound) {
        type = 'exclusion';
      } else {
        type = 'surplus_distribution';
      }
    }

    derivedRounds.push({
      roundNumber: round.roundNumber,
      type,
      actorId: round.actorId,
      perCandidate,
      nonTransferable: round.nonTransferable,
      totalsCheck: sumRunningTotals + round.nonTransferable,
    });

    // ── Carry forward ──
    // Excluded candidates drop out (their votes have moved); everyone
    // else (continuing or any elected/not-elected status) carries their
    // current rt to the next round for delta/total calculations.
    const nextCarry: Record<string, number> = {};
    for (const row of perCandidate) {
      if (row.status === 'excluded') continue;
      nextCarry[row.candidateId] = row.runningTotal;
    }
    prevByCand = nextCarry;
  }

  // electedSeats: ordered by election round; ties broken by round-1
  // running total descending (a stable, observable tiebreak — not a
  // decision).
  const electedSeats: string[] = Object.keys(electedAt)
    .filter(
      (cid) =>
        status[cid] === 'elected_by_quota' ||
        status[cid] === 'elected_without_quota',
    )
    .sort((a, b) => {
      if (electedAt[a] !== electedAt[b]) return electedAt[a] - electedAt[b];
      const r1 = sortedRounds[0]?.runningTotalByCandidate || {};
      return (r1[b] ?? 0) - (r1[a] ?? 0);
    })
    .slice(0, SEATS_PER_DISTRICT);

  const distStatus: DistrictStatus =
    sortedRounds.length === 0
      ? 'pending'
      : electedSeats.length >= SEATS_PER_DISTRICT
        ? 'complete'
        : 'in_progress';

  return {
    districtId,
    validVotes,
    quota,
    rounds: derivedRounds,
    electedSeats,
    status: distStatus,
  };
}

/** Internal helper — updates the running status map AND keeps the
 *  electedAt round map consistent (so electedSeats ordering reflects
 *  status flips, including overrides that revert an earlier election). */
function setStatus(
  status: Record<string, CandidateStatus>,
  electedAt: Record<string, number>,
  cid: string,
  next: CandidateStatus,
  round: number,
): void {
  status[cid] = next;
  if (next === 'elected_by_quota' || next === 'elected_without_quota') {
    if (electedAt[cid] === undefined) electedAt[cid] = round;
  } else {
    // Reverted out of an elected state — drop from the election order.
    if (electedAt[cid] !== undefined) delete electedAt[cid];
  }
}

function isCandidateStatus(v: unknown): v is CandidateStatus {
  return (
    typeof v === 'string' &&
    (CANDIDATE_STATUSES as readonly string[]).includes(v)
  );
}

// ── SANITY CHECK (lock condition) ─────────────────────────────────
//
// Optional, non-blocking, informational. With auto-derivation now in
// place, this helper exists to flag operator OVERRIDES that mark a
// candidate elected_without_quota when the lock condition doesn't
// hold — i.e. when the math wouldn't have elected them yet. The auto-
// derived without-quota elections always satisfy the lock by
// construction (PASS 2 only fires when it does), so this only ever
// fires on hand-typed overrides that look numerically odd.
//
// Same UX as the totals-check warning: surface loudly, block nothing.

export interface WithoutQuotaWarning {
  candidateId: string;
  round: number;
  leaderRt: number;
  threatRt: number;
  threatMaxIfAllTransferable: number;
  marginAfterTransfers: number;
  message: string;
}

export function checkWithoutQuotaConsistency(
  derived: DerivedDistrict,
): WithoutQuotaWarning[] {
  const warnings: WithoutQuotaWarning[] = [];
  const quota = derived.quota;
  if (quota === null) return warnings;

  let prevStatusById: Record<string, CandidateStatus> = {};
  for (const round of derived.rounds) {
    const curStatusById: Record<string, CandidateStatus> = {};
    for (const row of round.perCandidate) curStatusById[row.candidateId] = row.status;

    const newlyWithoutQuota = round.perCandidate.filter(
      (p) =>
        p.status === 'elected_without_quota' &&
        prevStatusById[p.candidateId] !== 'elected_without_quota',
    );

    if (newlyWithoutQuota.length > 0) {
      const threats = round.perCandidate.filter(
        (p) => p.status === 'continuing' || p.status === 'not_elected',
      );
      const pendingSurplus = round.perCandidate
        .filter((p) => p.status === 'elected_by_quota' && p.runningTotal > quota)
        .reduce((s, p) => s + (p.runningTotal - quota), 0);

      for (const winner of newlyWithoutQuota) {
        if (threats.length === 0) continue;
        const sortedThreats = [...threats].sort(
          (a, b) => b.runningTotal - a.runningTotal,
        );
        const top = sortedThreats[0];
        const others = sortedThreats
          .slice(1)
          .reduce((s, p) => s + p.runningTotal, 0);
        const threatMax = top.runningTotal + others + pendingSurplus;
        const margin = winner.runningTotal - threatMax;
        if (margin <= 0) {
          warnings.push({
            candidateId: winner.candidateId,
            round: round.roundNumber,
            leaderRt: winner.runningTotal,
            threatRt: top.runningTotal,
            threatMaxIfAllTransferable: threatMax,
            marginAfterTransfers: margin,
            message:
              `Elected without quota at R${round.roundNumber} with ` +
              `${winner.runningTotal} — but worst-case transfers could ` +
              `lift the highest non-elected candidate to ${threatMax} ` +
              `(margin ${margin > 0 ? '+' : ''}${margin}). Check the board.`,
          });
        }
      }
    }
    prevStatusById = curStatusById;
  }
  return warnings;
}

// ── LATEST-ELECTED CAROUSEL FEED ───────────────────────────────────
//
// Future surface: a carousel of the most-recently-elected candidates
// across all 13 districts (and similarly the most-recently-excluded).
// The data shape it consumes is purely derived from the existing
// stored state — no extra editor input needed. This helper walks
// every district's deriveDistrictCount() output and emits a single
// flat list, sorted most-recent-first by district + election round.
//
// Ordering rule: each district's electedSeats already comes back in
// election-order (status flipped earlier → ranked higher), so the
// last item in each district is the most-recent there. When merging
// districts we sort by the round at which each candidate was elected
// (later round = more recent), with district id as a stable tiebreak.

export interface LatestElectedEntry {
  candidateId: string;
  candidateName: string;
  party: string;
  districtId: number;
  /** Round number at which this candidate's status flipped to
   *  elected_by_quota or elected_without_quota. */
  electedAtRound: number;
  /** Running total at the round they were elected — for the cards
   *  to display "X votes". For elected_by_quota this equals the
   *  quota; for elected_without_quota it's their final standing. */
  votes: number;
}

/** Candidate-name + party lookup the helper needs. The caller
 *  passes whatever shape is convenient (typically the
 *  CandidateRow[] from /api/candidates), keyed by stringified id. */
export type CandidateLookup = Map<
  string,
  { full_name: string; party: string }
>;

/** Walk every district's derived count and emit the cross-district
 *  list of elected candidates, sorted most-recent-first.
 *
 *  Districts where no rounds have been entered yet contribute
 *  nothing. Districts where a status override has elected someone
 *  before any totals are entered (the "we know they're in" flow)
 *  contribute with electedAtRound = 1 + votes = 0. */
export function latestElectedAcrossDistricts(
  state: ResultsState | undefined,
  candidateRosterByDistrict: Map<number, readonly string[]>,
  lookup: CandidateLookup,
): LatestElectedEntry[] {
  if (!state) return [];

  const entries: LatestElectedEntry[] = [];
  for (const n of DISTRICT_NUMBERS) {
    const stored = state.districts[String(n)];
    if (!stored) continue;
    const candidateIds = candidateRosterByDistrict.get(n) ?? [];
    if (candidateIds.length === 0) continue;
    const derived = deriveDistrictCount(n, stored, candidateIds);

    // Walk the derived rounds — for each elected candidate, find
    // the earliest round where their status was elected_*. That's
    // the "elected at" round and the running total at that round
    // is the votes figure for the card.
    const electedSeenAt: Record<string, { round: number; votes: number }> = {};
    for (const round of derived.rounds) {
      for (const row of round.perCandidate) {
        if (
          (row.status === 'elected_by_quota' || row.status === 'elected_without_quota') &&
          electedSeenAt[row.candidateId] === undefined
        ) {
          electedSeenAt[row.candidateId] = {
            round: round.roundNumber,
            votes: row.runningTotal,
          };
        }
      }
    }
    for (const cid of derived.electedSeats) {
      const seen = electedSeenAt[cid];
      const cand = lookup.get(cid);
      entries.push({
        candidateId: cid,
        candidateName: cand?.full_name ?? cid,
        party: cand?.party ?? '',
        districtId: n,
        electedAtRound: seen?.round ?? 1,
        votes: seen?.votes ?? 0,
      });
    }
  }

  // Most-recent first: highest electedAtRound first, then highest
  // districtId (stable observable tiebreak — not a real "newer
  // than" claim, just a deterministic order across districts).
  entries.sort((a, b) => {
    if (b.electedAtRound !== a.electedAtRound) {
      return b.electedAtRound - a.electedAtRound;
    }
    return b.districtId - a.districtId;
  });
  return entries;
}
